import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Menu, 
  X, 
  Bell, 
  User, 
  Settings, 
  LogOut
} from 'lucide-react';
import { HexagonalLogo } from './Logo';
import { useAuth } from '../hooks/useAuth';
import OrganizationSwitcher from './OrganizationSwitcher';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleProfile = () => setIsProfileOpen(!isProfileOpen);

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
  };

  return (
    <nav className="bg-gray-800 border-b border-gray-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="/FullLogo_Transparent_NoBuffer.png" 
                alt="Dark Matter Solutions" 
                className="h-10 w-auto object-contain"
                style={{ minHeight: '40px' }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/' || location.pathname === '/marketing'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Marketing
            </Link>
            <Link
              to="/homepage"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/homepage'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Home
            </Link>
            <Link
              to="/dashboard"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/dashboard'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Dashboard
            </Link>
            <Link
              to="/compliance"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/compliance'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Compliance
            </Link>
            <Link
              to="/inventory"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/inventory'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Inventory
            </Link>
            <Link
              to="/iot-devices"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/iot-devices'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              IoT Devices
            </Link>
            <Link
              to="/atf-compliance"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/atf-compliance'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              ATF Compliance
            </Link>
            <Link
              to="/firearms-ingestion"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/firearms-ingestion'
                  ? 'text-blue-400'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Firearms
            </Link>
          </div>

          {/* Organization Switcher */}
          <div className="hidden md:flex items-center">
            <OrganizationSwitcher />
          </div>

          {/* Right side - Notifications and Profile */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="p-2 text-gray-400 hover:text-white transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                3
              </span>
            </button>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={toggleProfile}
                className="flex items-center space-x-2 p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="hidden md:block text-sm font-medium">
                  {user?.username || 'User'}
                </span>
              </button>

              {/* Profile Dropdown Menu */}
              {isProfileOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50"
                >
                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <User className="w-4 h-4 mr-3" />
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-gray-800 border-t border-gray-700"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/compliance"
              className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Compliance
            </Link>
            <Link
              to="/inventory"
              className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Inventory
            </Link>
            <Link
              to="/iot-devices"
              className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              IoT Devices
            </Link>
            <Link
              to="/firearms-ingestion"
              className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Firearms
            </Link>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
